/**
 * @xivdyetools/types - Branded Color Types
 *
 * Branded types prevent accidental type confusion between similar types.
 * For example, a DyeId cannot be accidentally used where a plain number is expected.
 *
 * ## TYPES-101: Runtime Validation Limitation
 *
 * **Important:** Branded types in TypeScript rely on runtime validation via helper
 * functions. The type system cannot enforce that all values are created through
 * these helpers. Developers can bypass validation using type assertions:
 *
 * ```typescript
 * // UNSAFE: Bypasses validation
 * const unsafeHex = "#invalid" as HexColor;
 *
 * // SAFE: Uses validation
 * const safeHex = createHexColor("#FF0000");
 * ```
 *
 * **Best Practice:** Always use the `create*` helper functions to ensure values
 * are validated. Never use type assertions (`as HexColor`) to create branded values.
 *
 * @module color/branded
 */
/**
 * Helper to create branded HexColor type with validation
 *
 * Validates hex format and normalizes to uppercase #RRGGBB format.
 *
 * @param hex - Input hex string (#RGB or #RRGGBB format)
 * @returns Normalized HexColor in #RRGGBB format
 * @throws {Error} If hex format is invalid
 *
 * @example
 * ```typescript
 * const red = createHexColor('#f00');      // Returns "#FF0000"
 * const blue = createHexColor('#0000ff');  // Returns "#0000FF"
 * createHexColor('invalid');               // Throws Error
 * ```
 */
export function createHexColor(hex) {
    // Validate format: must be #RGB or #RRGGBB
    if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)) {
        throw new Error(`Invalid hex color format: ${hex}. Expected #RRGGBB or #RGB format.`);
    }
    // Normalize short hex (#RGB) to long hex (#RRGGBB) and uppercase
    const normalized = hex.length === 4
        ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toUpperCase()
        : hex.toUpperCase();
    return normalized;
}
/**
 * Helper to create a branded DyeId with validation.
 *
 * 5.0 (schema v2): a `DyeId` is a **stainID** — the game's own dye number and
 * the canonical key of the dye table. The accepted window is the loader's
 * (1–254; 125 dyes today, room for future ones). Legacy market itemIDs
 * (5729…) and the pre-v2 synthetic negative Facewear ids are rejected —
 * Facewear colours are `FacewearColor`s, not dyes.
 *
 * @param id - stainID (1-254)
 * @returns Branded DyeId or null if invalid
 *
 * @example
 * ```typescript
 * const jetBlack = createDyeId(102);   // Returns 102 as DyeId
 * const invalid = createDyeId(0);      // Returns null
 * const legacy = createDyeId(5729);    // Returns null (itemID, not a stainID)
 * ```
 */
export function createDyeId(id) {
    if (!Number.isInteger(id)) {
        return null;
    }
    if (id < 1 || id > 254) {
        return null;
    }
    return id;
}
/**
 * Helper to create branded Hue type with normalization
 *
 * Normalizes hue to 0-360 range (wraps around).
 *
 * @param hue - Input hue value (any number, will be normalized)
 * @returns Normalized Hue in 0-360 range
 *
 * @example
 * ```typescript
 * createHue(45);    // Returns 45
 * createHue(400);   // Returns 40 (wrapped)
 * createHue(-30);   // Returns 330 (wrapped)
 * ```
 */
export function createHue(hue) {
    // Normalize to 0-360 range
    const normalized = ((hue % 360) + 360) % 360;
    return normalized;
}
/**
 * Helper to create branded Saturation type with clamping
 *
 * Clamps saturation to 0-100 range.
 *
 * @param saturation - Input saturation value
 * @returns Clamped Saturation in 0-100 range
 *
 * @example
 * ```typescript
 * createSaturation(50);   // Returns 50
 * createSaturation(150);  // Returns 100 (clamped)
 * createSaturation(-10);  // Returns 0 (clamped)
 * ```
 */
export function createSaturation(saturation) {
    const clamped = Math.max(0, Math.min(100, saturation));
    return clamped;
}
//# sourceMappingURL=branded.js.map